/**
 * API Keys management — generate, view, and revoke organization API keys
 * for MCP server, CI/CD, and external tool access.
 */

import React, { useEffect, useState, useCallback } from "react";

import * as api from "../api.js";
import { PageHeader, Loading, ErrorBanner, formatDate } from "../components.jsx";
import { useAuth } from "../AuthContext.jsx";

export default function ApiKeys() {
  const { currentOrgSlug } = useAuth();
  const orgSlug = currentOrgSlug;
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState({ name: "", role: "editor" });
  const [createdKey, setCreatedKey] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!orgSlug) return;
    setLoading(true);
    try {
      const res = await api.listApiKeys(orgSlug);
      setKeys(res.data || []);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [orgSlug]);

  useEffect(() => { load(); }, [load]);

  const createKey = async () => {
    if (!newKey.name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.createApiKey(orgSlug, {
        name: newKey.name.trim(),
        role: newKey.role,
      });
      setCreatedKey(res.data);
      setNewKey({ name: "", role: "editor" });
      setShowCreate(false);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const revokeKey = async (keyId) => {
    if (!window.confirm("Revoke this API key? Any tools using it will stop working immediately.")) return;
    setBusy(true);
    try {
      await api.revokeApiKey(orgSlug, keyId);
      setCreatedKey(null);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <>
      <PageHeader
        title="API Keys"
        subtitle="Generate keys for MCP server (Claude Code), CI/CD pipelines, and external tools."
        breadcrumbs={[
          { label: "Organization" },
          { label: "API Keys" },
        ]}
        actions={
          <button
            className="btn primary"
            onClick={() => setShowCreate(!showCreate)}
          >
            {showCreate ? "Cancel" : "+ Generate Key"}
          </button>
        }
      />

      <div className="page-body">
        <ErrorBanner error={error} />

        {/* Just-created key — show ONCE */}
        {createdKey && (
          <div
            className="card"
            style={{
              background: "#fff8e1",
              borderColor: "#ffc107",
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span style={{ fontSize: 20 }}>🔑</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong>API key created — copy it now!</strong>
                <p className="muted small" style={{ margin: "4px 0" }}>
                  This key will only be shown once. Store it securely.
                </p>
                <div
                  style={{
                    padding: "10px 14px",
                    background: "#fff",
                    border: "1px solid #e0e0e0",
                    borderRadius: 6,
                    fontFamily: "var(--font-mono)",
                    fontSize: 14,
                    wordBreak: "break-all",
                    marginTop: 8,
                    userSelect: "all",
                  }}
                >
                  {createdKey.key}
                </div>
                <div className="btn-row" style={{ marginTop: 8 }}>
                  <button
                    className="btn sm"
                    onClick={() => {
                      navigator.clipboard.writeText(createdKey.key);
                    }}
                  >
                    Copy to clipboard
                  </button>
                  <button
                    className="btn sm"
                    onClick={() => setCreatedKey(null)}
                  >
                    Dismiss
                  </button>
                </div>
                <div className="muted small" style={{ marginTop: 8 }}>
                  <strong>MCP server config:</strong>
                  <pre
                    style={{
                      margin: "4px 0",
                      padding: 8,
                      background: "#f5f5f5",
                      borderRadius: 4,
                      fontSize: 11,
                      whiteSpace: "pre-wrap",
                    }}
                  >{`{
  "mcpServers": {
    "configural": {
      "command": "npx",
      "args": ["configural-mcp"],
      "env": {
        "CONFIGURAL_API_URL": "${window.location.origin}",
        "CONFIGURAL_API_KEY": "${createdKey.key}"
      }
    }
  }
}`}</pre>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create form */}
        {showCreate && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <h3>Generate new API key</h3>
            </div>
            <div className="agent-config-grid">
              <div className="form-field">
                <label>Name</label>
                <input
                  type="text"
                  value={newKey.name}
                  onChange={(e) =>
                    setNewKey((k) => ({ ...k, name: e.target.value }))
                  }
                  placeholder="e.g. Claude Code - Dev"
                />
              </div>
              <div className="form-field">
                <label>Role</label>
                <select
                  value={newKey.role}
                  onChange={(e) =>
                    setNewKey((k) => ({ ...k, role: e.target.value }))
                  }
                >
                  <option value="viewer">Viewer — read-only access</option>
                  <option value="editor">Editor — read + write to draft</option>
                  <option value="admin">Admin — full access including publish + delete</option>
                </select>
              </div>
            </div>
            <button
              className="btn primary sm"
              onClick={createKey}
              disabled={busy || !newKey.name.trim()}
              style={{ marginTop: 12 }}
            >
              {busy ? "Generating..." : "Generate Key"}
            </button>
          </div>
        )}

        {/* Keys table */}
        <div className="card" style={{ padding: 0 }}>
          <table className="table" style={{ border: "none" }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Key prefix</th>
                <th>Role</th>
                <th>Created by</th>
                <th>Last used</th>
                <th>Uses</th>
                <th>Status</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {keys.length === 0 ? (
                <tr>
                  <td colSpan={8} className="table-empty">
                    No API keys. Click "+ Generate Key" to create one.
                  </td>
                </tr>
              ) : (
                keys.map((k) => (
                  <tr
                    key={k._id}
                    style={{ opacity: k.revoked ? 0.5 : 1 }}
                  >
                    <td>
                      <strong>{k.name}</strong>
                    </td>
                    <td className="mono small">{k.prefix}</td>
                    <td>
                      <span
                        className={`badge ${
                          k.role === "admin"
                            ? "badge-danger"
                            : k.role === "editor"
                            ? "badge-accent"
                            : "badge-neutral"
                        }`}
                      >
                        {k.role}
                      </span>
                    </td>
                    <td className="muted small">{k.created_by}</td>
                    <td className="muted small">
                      {k.last_used_at
                        ? formatDate(k.last_used_at)
                        : "Never"}
                    </td>
                    <td className="mono small">{k.use_count}</td>
                    <td>
                      {k.revoked ? (
                        <span className="badge badge-stale">Revoked</span>
                      ) : (
                        <span className="badge badge-live">Active</span>
                      )}
                    </td>
                    <td>
                      {!k.revoked && (
                        <button
                          className="btn sm"
                          style={{ color: "#c62828" }}
                          onClick={() => revokeKey(k._id)}
                          disabled={busy}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
